export const MAX_FOLDER_DEPTH = 5;

export function normalizeParentId(value) {
  if (value == null || value === '' || value === 'null') return null;
  return String(value);
}

export function buildFolderTree(items, { idKey = 'id', parentKey = 'parentId' } = {}) {
  const nodes = new Map();
  for (const item of items || []) {
    if (!item || item[idKey] == null) continue;
    nodes.set(String(item[idKey]), { ...item, children: [] });
  }
  const roots = [];
  for (const node of nodes.values()) {
    const parentId = normalizeParentId(node[parentKey]);
    const parent = parentId ? nodes.get(parentId) : null;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}

export function flattenFolderTree(nodes, { expandedIds = null, depth = 0 } = {}) {
  const rows = [];
  for (const node of nodes || []) {
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    rows.push({ ...node, depth, hasChildren });
    const expanded = !expandedIds || expandedIds.has(node.id);
    if (hasChildren && expanded) {
      rows.push(...flattenFolderTree(node.children, { expandedIds, depth: depth + 1 }));
    }
  }
  return rows;
}

export function getAncestorIds(items, id, { idKey = 'id', parentKey = 'parentId' } = {}) {
  const byId = new Map((items || []).filter(Boolean).map((item) => [String(item[idKey]), item]));
  const ids = [];
  let current = byId.get(String(id));
  const seen = new Set();
  while (current) {
    const parentId = normalizeParentId(current[parentKey]);
    if (!parentId || seen.has(parentId)) break;
    seen.add(parentId);
    ids.unshift(parentId);
    current = byId.get(parentId);
  }
  return ids;
}

export function getDescendantIds(items, id, { idKey = 'id', parentKey = 'parentId' } = {}) {
  const childrenByParent = new Map();
  for (const item of items || []) {
    if (!item || item[idKey] == null) continue;
    const parentId = normalizeParentId(item[parentKey]);
    if (!parentId) continue;
    if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
    childrenByParent.get(parentId).push(String(item[idKey]));
  }
  const ids = [];
  const stack = [...(childrenByParent.get(String(id)) || [])];
  const seen = new Set(stack);
  while (stack.length) {
    const childId = stack.pop();
    ids.push(childId);
    for (const next of childrenByParent.get(childId) || []) {
      if (seen.has(next)) continue;
      seen.add(next);
      stack.push(next);
    }
  }
  return ids;
}

export function getItemDepth(items, id, { idKey = 'id', parentKey = 'parentId' } = {}) {
  return getAncestorIds(items, id, { idKey, parentKey }).length;
}

export function sortFolderTree(nodes, compareFn) {
  const sorted = [...(nodes || [])].sort(compareFn);
  return sorted.map((node) => ({
    ...node,
    children: sortFolderTree(node.children, compareFn),
  }));
}
